// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//     http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.
// ========= Copyright 2025-2026 @ Eigent.ai All Rights Reserved. =========

import { MorphIcon } from '@/components/ui/morph-icon';
import { cn } from '@/lib/utils';
import type { WorkItemStatus } from '@web/lib/remoteWorkItemModel';
import { Activity, CircleAlert, CircleHelp, Loader2 } from 'lucide';
import { useEffect, useMemo, useRef, useState } from 'react';
import { fallbackActivityText } from './presentation';

const ANNOUNCEMENT_COALESCE_MS = 350;

export interface LiveActivityBarProps {
  status: WorkItemStatus;
  summary?: string | null;
  agentName?: string | null;
  activeAgentCount?: number;
  className?: string;
}

function ActivityGlyph({ status }: { status: WorkItemStatus }) {
  const className = 'h-4 w-4 shrink-0';
  if (status === 'in_progress') {
    return (
      <MorphIcon
        icon={Loader2}
        aria-hidden
        className={cn(className, 'animate-spin')}
      />
    );
  }
  if (status === 'waiting') {
    return <MorphIcon icon={CircleHelp} aria-hidden className={className} />;
  }
  if (status === 'failed' || status === 'interrupted') {
    return <MorphIcon icon={CircleAlert} aria-hidden className={className} />;
  }
  return <MorphIcon icon={Activity} aria-hidden className={className} />;
}

function buildActivityLabel({
  status,
  summary,
  agentName,
  activeAgentCount,
}: Omit<LiveActivityBarProps, 'className'>): string {
  const activity = summary?.trim() || fallbackActivityText(status);
  const owner = agentName?.trim();

  if ((activeAgentCount ?? 0) > 1) {
    return `${activeAgentCount} agents working · ${owner ? `${owner} · ` : ''}${activity}`;
  }
  return owner ? `${owner} · ${activity}` : activity;
}

export function LiveActivityBar({
  status,
  summary,
  agentName,
  activeAgentCount = 0,
  className,
}: LiveActivityBarProps) {
  const visibleLabel = useMemo(
    () => buildActivityLabel({ status, summary, agentName, activeAgentCount }),
    [activeAgentCount, agentName, status, summary]
  );
  const [announcement, setAnnouncement] = useState(visibleLabel);
  const pendingAnnouncementRef = useRef(visibleLabel);
  const lastAnnouncementAtRef = useRef(Date.now());
  const announcementTimerRef = useRef<number | null>(null);

  useEffect(() => {
    pendingAnnouncementRef.current = visibleLabel;
    if (visibleLabel === announcement) return;
    if (announcementTimerRef.current != null) return;
    const elapsed = Date.now() - lastAnnouncementAtRef.current;
    announcementTimerRef.current = window.setTimeout(
      () => {
        announcementTimerRef.current = null;
        lastAnnouncementAtRef.current = Date.now();
        setAnnouncement(pendingAnnouncementRef.current);
      },
      Math.max(0, ANNOUNCEMENT_COALESCE_MS - elapsed)
    );
  }, [announcement, visibleLabel]);

  useEffect(
    () => () => {
      if (announcementTimerRef.current != null) {
        window.clearTimeout(announcementTimerRef.current);
      }
    },
    []
  );

  return (
    <div
      className={cn(
        'flex min-w-0 items-center gap-2 rounded-xl border border-solid border-ds-border-neutral-subtle-default bg-ds-bg-neutral-subtle-default px-3 py-2 text-ds-text-neutral-muted-default',
        className
      )}
    >
      <ActivityGlyph status={status} />
      <p
        className="m-0 min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-body-sm"
        title={visibleLabel}
      >
        {visibleLabel}
      </p>
      <span className="sr-only" role="status" aria-live="polite" aria-atomic>
        {announcement}
      </span>
    </div>
  );
}
